import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AvatarPreview } from "@/components/AvatarPreview";
import { ChatPanel } from "@/components/ChatPanel";
import { MintPanel } from "@/components/MintPanel";
import { TraitBuilder } from "@/components/TraitBuilder";
import { useWallet } from "@/context/WalletContext";
import { loadDraft, saveDraft } from "@/lib/browser-store";
import { DEFAULT_TRAITS, type PersonalityCard, type TraitSelection } from "@/types";

export function CreatePage() {
  const { address } = useWallet();
  const navigate = useNavigate();
  const [traits, setTraits] = useState<TraitSelection>(DEFAULT_TRAITS);
  const [personality, setPersonality] = useState<PersonalityCard | null>(null);
  const [speaking, setSpeaking] = useState(false);

  useEffect(() => {
    if (!address) return;
    let active = true;
    loadDraft(address).then((draft) => {
      if (active && draft) setTraits(draft);
    }).catch(() => undefined);
    return () => { active = false; };
  }, [address]);

  useEffect(() => {
    if (address) void saveDraft(address, traits);
  }, [address, traits]);

  return (
    <main className="mx-auto max-w-[1480px] px-4 py-8 sm:px-6 lg:py-10">
      <div className="mb-8 flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
        <div><p className="text-[11px] font-bold tracking-[.22em] text-cyan-200/65">SOUL FORGE // DRAFT</p><h1 className="mt-2 text-3xl font-black tracking-tight sm:text-4xl">Build something nobody else can own.</h1></div>
        <p className="max-w-md text-sm leading-6 text-white/42">Private chat stays in this browser. Only the approved 512-byte personality card goes on-chain.</p>
      </div>
      <div className="grid gap-5 xl:grid-cols-[minmax(320px,.78fr)_minmax(430px,1.1fr)_minmax(360px,.82fr)]">
        <div className="space-y-5">
          <AvatarPreview speaking={speaking} traits={traits} />
          <TraitBuilder onChange={setTraits} traits={traits} />
        </div>
        <ChatPanel onPersonality={setPersonality} onSpeaking={setSpeaking} personality={personality} traits={traits} />
        <div className="space-y-5">
          <section className="rounded-[24px] border border-white/10 bg-white/[.035] p-5">
            <p className="text-[11px] font-bold tracking-[.2em] text-pink-300/70">PUBLIC PERSONALITY</p>
            {personality ? (
              <div className="mt-4 space-y-4">
                <div><h2 className="text-2xl font-black">{personality.name}</h2><p className="mt-1 text-sm text-white/48">{personality.voice}</p></div>
                <div className="flex flex-wrap gap-2">{[...personality.values, ...personality.likes].map((value) => <span className="rounded-full border border-white/10 bg-white/5 px-2.5 py-1 text-[11px] text-white/60" key={value}>{value}</span>)}</div>
                <p className="text-sm leading-6 text-white/55">{personality.lore}</p>
                <button className="text-xs text-cyan-200 hover:underline" onClick={() => setPersonality(null)} type="button">Refine with another chat</button>
              </div>
            ) : <p className="mt-4 text-sm leading-6 text-white/40">Complete five conversation turns, then AniSoul will turn them into a short card for you to approve.</p>}
          </section>
          <MintPanel onMinted={(tokenId) => navigate(`/avatar/${tokenId}`)} personality={personality} traits={traits} />
        </div>
      </div>
    </main>
  );
}
